import { useState, useEffect } from 'react';
import type { User } from '@/services/api';
import { userApi, groupApi, teamApi } from '@/services/api';

interface UsersTabProps {
  users: User[];
  maxUsers: number;
  resendState: Record<string, 'sending' | 'sent'>;
  onAdd: () => void;
  onEdit: (user: User) => void;
  onResend: (userId: string) => void;
  onRefresh: () => void;
}

interface Group { id: string; name: string; }
interface Team { id: string; name: string; groupId?: string | null; }

export function UsersTab({ users, maxUsers, resendState, onAdd, onEdit, onResend, onRefresh }: UsersTabProps) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  useEffect(() => {
    Promise.all([groupApi.getAll(), teamApi.getAll()])
      .then(([g, t]) => { setGroups(g); setTeams(t); })
      .catch(() => {/* non-fatal */});
  }, []);

  const atLimit = users.length >= maxUsers;
  const nearLimit = !atLimit && users.length / maxUsers >= 0.8;
  const countColor = atLimit ? 'text-red-600' : nearLimit ? 'text-amber-600' : 'text-text-secondary';

  const handleGroupChange = async (user: User, newGroupId: string) => {
    const groupId = newGroupId || null;
    // If the user's current team doesn't belong to the new group, clear teamId
    const currentTeam = teams.find(t => t.id === user.teamId);
    const teamBelongsToGroup = currentTeam && currentTeam.groupId === groupId;
    const teamId = teamBelongsToGroup ? user.teamId : null;

    setSaving(prev => ({ ...prev, [user.id]: true }));
    try {
      await userApi.update(user.id, { groupId, teamId });
      onRefresh();
    } catch {
      // silently fail — user can retry
    } finally {
      setSaving(prev => ({ ...prev, [user.id]: false }));
    }
  };

  const handleTeamChange = async (user: User, newTeamId: string) => {
    const teamId = newTeamId || null;
    setSaving(prev => ({ ...prev, [user.id]: true }));
    try {
      await userApi.update(user.id, { teamId });
      onRefresh();
    } catch {
      // silently fail
    } finally {
      setSaving(prev => ({ ...prev, [user.id]: false }));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-text-primary">Users</h2>
          <span className={`text-xs ${countColor}`}>
            {users.length} of {maxUsers}
            {atLimit && ' · limit reached'}
            {nearLimit && ' · approaching limit'}
          </span>
        </div>
        <button
          onClick={onAdd}
          disabled={atLimit}
          title={atLimit ? 'User limit reached' : undefined}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          + Add user
        </button>
      </div>
      <div className="border border-border-default rounded-md overflow-hidden">
        <table className="w-full border-collapse">
          <thead className="bg-background-secondary">
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-4 text-xs font-semibold text-text-secondary">Name</th>
              <th className="text-left py-2 px-4 text-xs font-semibold text-text-secondary">Email</th>
              <th className="text-left py-2 px-4 text-xs font-semibold text-text-secondary">Title</th>
              <th className="text-left py-2 px-4 text-xs font-semibold text-text-secondary">Role</th>
              <th className="text-left py-2 px-4 text-xs font-semibold text-text-secondary">Group</th>
              <th className="text-left py-2 px-4 text-xs font-semibold text-text-secondary">Team</th>
              <th className="text-left py-2 px-4 text-xs font-semibold text-text-secondary whitespace-nowrap">Last Login</th>
              <th className="text-right py-2 px-4 text-xs font-semibold text-text-secondary">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const isPending = !!user.isPending;
              const resend = resendState[user.id];
              const isSaving = saving[user.id];
              // Filter teams to those belonging to this user's group
              const availableTeams = user.groupId ? teams.filter(t => t.groupId === user.groupId) : teams;
              return (
                <tr key={user.id} className="border-b border-gray-100 last:border-0 hover:bg-surface-hover">
                  <td className="py-2 px-4 text-sm text-text-primary font-medium whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {user.name}
                      {isPending && (
                        <span className="inline-block px-1.5 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-700">Pending</span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-4 text-xs text-text-secondary">{user.email}</td>
                  <td className="py-2 px-4 text-xs text-text-secondary">{user.title || '—'}</td>
                  <td className="py-2 px-4">
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                      user.role === 'ADMIN' ? 'bg-purple-100 text-purple-700'
                        : user.role === 'EDITOR' ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="py-2 px-4">
                    <select
                      value={user.groupId || ''}
                      disabled={isSaving}
                      onChange={e => handleGroupChange(user, e.target.value)}
                      className="w-32 text-xs border border-border-default rounded px-1.5 py-0.5 bg-background-primary text-text-secondary focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-60"
                    >
                      <option value="">—</option>
                      {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </td>
                  <td className="py-2 px-4">
                    <select
                      value={user.teamId || ''}
                      disabled={isSaving || !user.groupId}
                      onChange={e => handleTeamChange(user, e.target.value)}
                      className="w-32 text-xs border border-border-default rounded px-1.5 py-0.5 bg-background-primary text-text-secondary focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-60"
                      title={!user.groupId ? 'Select a group first' : undefined}
                    >
                      <option value="">—</option>
                      {availableTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </td>
                  <td className="py-2 px-4 text-xs text-text-secondary whitespace-nowrap">
                    {user.lastLoginAt
                      ? new Date(user.lastLoginAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : 'Never'}
                  </td>
                  <td className="py-2 px-4 text-right">
                    <div className="flex items-center justify-end gap-3">
                      {isPending && (
                        <button
                          onClick={() => onResend(user.id)}
                          disabled={!!resend}
                          className={`text-xs ${resend === 'sent' ? 'text-green-600' : 'text-text-secondary hover:text-text-primary'} disabled:cursor-not-allowed`}
                        >
                          {resend === 'sending' ? 'Sending...' : resend === 'sent' ? 'Sent!' : 'Resend'}
                        </button>
                      )}
                      <button onClick={() => onEdit(user)} className="text-xs text-blue-600 hover:text-blue-700 font-medium">Edit</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {users.length === 0 && (
          <p className="py-6 text-center text-sm text-text-secondary">No users yet.</p>
        )}
      </div>
    </div>
  );
}
